import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Check, ChevronRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BetTypeSheet } from "./BetTypeSheet";
import { useCouponCart } from "@/stores/couponCart";
import { useUi } from "@/stores/ui";
import { formatTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { Match } from "@/types/domain";

interface Props {
  match: Match;
  compact?: boolean;
}

export function MatchCard({ match, compact }: Props) {
  const [betOpen, setBetOpen] = useState(false);
  const { type, hasMatch, removeBet, getBet } = useCouponCart();
  const { setCouponSheetOpen } = useUi();
  const inCart = hasMatch(match.id);
  const currentBet = getBet(match.id);
  const started = !["NS", "PST", "CANC"].includes(match.status);
  const live = ["1H", "2H", "HT", "ET"].includes(match.status);
  const finished = ["FT", "AET", "PEN"].includes(match.status);

  function onPick() {
    if (started) return;
    if (!type) {
      setCouponSheetOpen(true);
      return;
    }
    if (inCart) {
      removeBet(match.id);
      return;
    }
    setBetOpen(true);
  }

  // Cotes "simulées" pour l'UI (TradingView-style)
  // À remplacer par cotes réelles quand on aura la source
  const odds = pseudoOdds(match.id);

  return (
    <>
      <article
        className={cn(
          "group rounded-xl border border-white/[0.06] bg-card overflow-hidden transition-colors duration-180",
          inCart && "border-primary/40",
          compact ? "p-3" : "p-4"
        )}
      >
        {/* Header : ligue + heure / status */}
        <div className="flex items-center justify-between mb-3">
          <Link
            to={`/match/${match.id}`}
            className="flex items-center gap-1.5 min-w-0 group/lig"
            onClick={(e) => e.stopPropagation()}
          >
            {match.league.logo && (
              <img src={match.league.logo} alt="" className="size-3.5 object-contain opacity-80" loading="lazy" />
            )}
            <span className="text-2xs font-medium text-muted-foreground truncate group-hover/lig:text-foreground transition-colors">
              {match.league.name}
            </span>
            <ChevronRight className="size-3 text-muted-foreground/60" />
          </Link>
          <StatusTag status={match.status} elapsed={match.elapsed} kickoff={match.kickoff} live={live} finished={finished} />
        </div>

        {/* Teams + scores */}
        <div className="space-y-1.5">
          <TeamRow team={match.home} score={match.score.home} accent={!!finished && (match.score.home ?? 0) > (match.score.away ?? 0)} />
          <TeamRow team={match.away} score={match.score.away} accent={!!finished && (match.score.away ?? 0) > (match.score.home ?? 0)} />
        </div>

        {/* Cotes grid (3 colonnes : 1 / X / 2) — TradingView style */}
        {!started && (
          <div className="mt-3 grid grid-cols-3 gap-1.5">
            <OddPill label="1" odd={odds.home} active={currentBet?.bet.type === "result" && currentBet.bet.pick === "1"} onClick={() => quickPick("1")} />
            <OddPill label="X" odd={odds.draw} active={currentBet?.bet.type === "result" && currentBet.bet.pick === "X"} onClick={() => quickPick("X")} />
            <OddPill label="2" odd={odds.away} active={currentBet?.bet.type === "result" && currentBet.bet.pick === "2"} onClick={() => quickPick("2")} />
          </div>
        )}

        {/* Footer : action principale */}
        {!started ? (
          <div className="mt-3 flex items-center gap-2">
            <Button
              onClick={onPick}
              variant={inCart ? "outline" : "secondary"}
              size="sm"
              className="flex-1"
            >
              {inCart ? (
                <>
                  <Check className="size-3.5" /> Dans le coupon
                </>
              ) : (
                <>
                  <Plus className="size-3.5" /> Plus d'événements
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="mt-3 flex items-center justify-center gap-1.5 text-2xs text-muted-foreground">
            <Lock className="size-3" />
            <span>Événement fermé</span>
          </div>
        )}
      </article>

      <BetTypeSheet open={betOpen} onOpenChange={setBetOpen} match={match} />
    </>
  );

  function quickPick(pick: "1" | "X" | "2") {
    if (started) return;
    if (!type) {
      setCouponSheetOpen(true);
      return;
    }
    const cart = useCouponCart.getState();
    if (cart.hasMatch(match.id)) {
      cart.updateBet(match.id, { type: "result", pick });
    } else {
      cart.addBet(match, { type: "result", pick });
    }
  }
}

function TeamRow({ team, score, accent }: { team: { name: string; logo: string }; score: number | null; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="size-6 rounded-md bg-white shrink-0 flex items-center justify-center p-0.5">
          {team.logo ? (
            <img src={team.logo} alt="" className="size-full object-contain" loading="lazy" />
          ) : (
            <span className="text-[8px] font-bold text-black">{team.name.slice(0, 2)}</span>
          )}
        </div>
        <span className={cn("text-sm font-medium truncate", accent && "font-semibold")}>
          {team.name}
        </span>
      </div>
      {score !== null && (
        <span className={cn("text-base font-semibold num tabular-nums", accent ? "text-foreground" : "text-muted-foreground")}>
          {score}
        </span>
      )}
    </div>
  );
}

function StatusTag({
  status,
  elapsed,
  kickoff,
  live,
  finished,
}: {
  status: string;
  elapsed: number | null;
  kickoff: string;
  live: boolean;
  finished: boolean;
}) {
  if (live) {
    const label = status === "HT" ? "MT" : status === "ET" ? "PROL." : `${elapsed}'`;
    return (
      <span className="inline-flex items-center text-2xs font-semibold text-destructive pulse-dot">
        {label}
      </span>
    );
  }
  if (finished) {
    return <span className="text-2xs font-medium text-muted-foreground">Terminé</span>;
  }
  return <span className="text-2xs font-medium text-muted-foreground tabular-nums">{formatTime(kickoff)}</span>;
}

function OddPill({ label, odd, active, onClick }: { label: string; odd: number; active?: boolean; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "h-11 rounded-md flex items-center justify-between px-3 transition-all duration-180",
        "border border-white/[0.06] bg-white/[0.02]",
        "hover:border-white/[0.12] hover:bg-white/[0.04]",
        "active:scale-[0.98]",
        active && "border-primary/60 bg-primary/[0.08] text-primary"
      )}
    >
      <span className={cn("text-2xs font-semibold uppercase", active ? "text-primary" : "text-muted-foreground")}>
        {label}
      </span>
      <span className={cn("text-sm font-semibold num", active ? "text-primary" : "text-foreground")}>
        {odd.toFixed(2)}
      </span>
    </button>
  );
}

// Cotes pseudo-aléatoires déterministes basées sur l'ID — placeholder visuel
function pseudoOdds(id: number): { home: number; draw: number; away: number } {
  const seed = (n: number, salt: number) => {
    const x = Math.sin(n * 9999 + salt) * 10000;
    return x - Math.floor(x);
  };
  const home = 1.4 + seed(id, 1) * 3.5;
  const away = 1.4 + seed(id, 2) * 3.5;
  const draw = 2.8 + seed(id, 3) * 1.5;
  return { home: round2(home), draw: round2(draw), away: round2(away) };
}
function round2(n: number) {
  return Math.round(n * 100) / 100;
}
